import '@src/styles/rdr/styles.css';
import '@src/styles/rdr/default.css';

import React from 'react';
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import {DateRangePicker as BaseDateRangePicker, createStaticRanges} from "react-date-range";

const toDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

function DateRangePicker({ startDate, endDate, onChange }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const safeStartDate = React.useMemo(() => toDate(startDate) ?? new Date(), [startDate]);
  const safeEndDate = React.useMemo(
    () => {
      const parsedEnd = toDate(endDate);
      if (!parsedEnd) {
        return safeStartDate;
      }

      return parsedEnd < safeStartDate ? safeStartDate : parsedEnd;
    },
    [endDate, safeStartDate],
  );
  const selectionRange = React.useMemo(
    () => ({
      startDate: safeStartDate,
      endDate: safeEndDate,
      key: 'selection',
    }),
    [safeStartDate, safeEndDate],
  );
  const staticRanges = React.useMemo(() => {
    const startOfDay = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };
    const endOfDay = (date) => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999);
      return d;
    };
    const addDays = (date, amount) => {
      const d = new Date(date);
      d.setDate(d.getDate() + amount);
      return d;
    };
    const startOfWeek = (date) => {
      const d = startOfDay(date);
      const day = d.getDay();
      d.setDate(d.getDate() - day);
      return d;
    };
    const endOfWeek = (date) => {
      const d = startOfWeek(date);
      d.setDate(d.getDate() + 6);
      d.setHours(23, 59, 59, 999);
      return d;
    };
    const startOfMonth = (date) => {
      const d = startOfDay(date);
      d.setDate(1);
      return d;
    };
    const endOfMonth = (date) => {
      const d = startOfMonth(date);
      d.setMonth(d.getMonth() + 1);
      d.setDate(0);
      d.setHours(23, 59, 59, 999);
      return d;
    };
    const buildPreviousDaysRange = (days) => {
      const today = new Date();
      const end = endOfDay(addDays(today, -1));
      const start = startOfDay(addDays(end, -(days - 1)));
      return { startDate: start, endDate: end };
    };

    return createStaticRanges([
      {
        label: 'Today',
        range: () => {
          const now = new Date();
          return { startDate: startOfDay(now), endDate: endOfDay(now) };
        },
      },
      {
        label: 'Yesterday',
        range: () => {
          const now = new Date();
          const y = addDays(now, -1);
          return { startDate: startOfDay(y), endDate: endOfDay(y) };
        },
      },
      {
        label: 'This Week',
        range: () => {
          const now = new Date();
          return { startDate: startOfWeek(now), endDate: endOfWeek(now) };
        },
      },
      {
        label: 'Last Week',
        range: () => {
          const now = addDays(new Date(), -7);
          return { startDate: startOfWeek(now), endDate: endOfWeek(now) };
        },
      },
      {
        label: 'This Month',
        range: () => {
          const now = new Date();
          return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
        },
      },
      {
        label: 'Last Month',
        range: () => {
          const now = new Date();
          now.setMonth(now.getMonth() - 1);
          return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
        },
      },
      {
        label: 'Last 30 Days',
        range: () => buildPreviousDaysRange(30),
      },
      {
        label: 'Last 60 Days',
        range: () => buildPreviousDaysRange(60),
      },
      {
        label: 'Last 90 Days',
        range: () => buildPreviousDaysRange(90),
      },
    ]);
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const formatDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return 'Select range';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  const handleChange = (e) => {
    if (typeof onChange === 'function') {
      onChange(e);
    }
    setAnchorEl(null);
  }

  return (
    <React.Fragment>
      <Button aria-describedby={id} variant="contained" color="primary" onClick={handleClick}>
        Date: {formatDate(safeStartDate)} - {formatDate(safeEndDate)}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <BaseDateRangePicker
          ranges={[selectionRange]}
          onChange={handleChange}
          staticRanges={staticRanges}
          inputRanges={[]}
        />
      </Popover>
    </React.Fragment>
  );
}

export default DateRangePicker;
